import * as React from 'react';
import classNames from 'classnames';
import {
  BoundedContent,
  Box,
  StandardTile,
  StandardTileContents,
  StandardTileDescription,
  StandardTileImage,
  StandardTileName,
  StandardTileOverlay,
  Typography,
} from '@vp/swan';
import { Image } from '../../../Image';
import TrackableLink from '../../../TrackableLink';
import { BookendsBadge } from '../../../BookendsBadge';
import { FlyoutWrapper } from './FlyoutWrapper';
import { useIsSsrContext } from '../../../../contexts/IsSsrContext';
import {
  LinkList,
  NavItem,
  TileProps,
  TileListNavFlyoutProps,
} from '../../../../types';
import { useNavContext } from '../NavContext';
import { SeeAllBar } from './SeeAllBar';

export const TileListNavFlyout = (
  props: TileListNavFlyoutProps &
    LinkList<NavItem> &
    React.ComponentProps<'section'>
) => {
  const { isMxNav } = useNavContext();
  const { markFlyoutHovered, isFlyoutHovered } = useIsSsrContext();
  const flyoutId = props.id || `tile-list-flyout-${props.parentNavigationDetailPath.join('-')}`;

  // Treat the flyout as "hovered" if either:
  // 1. It has been explicitly hovered before, OR
  // 2. It is currently in the focused category (prevents FOUC on quick switches)
  const hasBeenHovered = isFlyoutHovered(flyoutId) || props.isInFocusedCategory;

  // Track if this is the first render of the flyout (for CSS animation)
  const [isFirstRender, setIsFirstRender] = React.useState(true);

  // Mark flyout as hovered when parent nav item is focused (for lazy-rerender mode)
  React.useEffect(() => {
    if (props.isInFocusedCategory && !isFlyoutHovered(flyoutId)) {
      markFlyoutHovered(flyoutId);
    }
  }, [props.isInFocusedCategory, flyoutId, isFlyoutHovered, markFlyoutHovered]);

  // Clear first render flag after mount to allow CSS animations
  React.useEffect(() => {
    if (hasBeenHovered && isFirstRender) {
      // Use requestAnimationFrame to ensure the initial hidden state is painted before animating
      requestAnimationFrame(() => {
        setIsFirstRender(false);
      });
    }
  }, [hasBeenHovered, isFirstRender]);

  if (!props.children) {
    return null;
  }

  // Render full flyout content (used by FlyoutWrapper when appropriate)
  const renderFullFlyout = () => (
    <section
      className={classNames(
        'site-header-nav-flyout site-header-nav-flyout-tile-list',
        props.className
      )}
      id={props.id}
      aria-labelledby={props.labelledBy}
      data-first-render={isFirstRender ? 'true' : undefined}
    >
      <BoundedContent style={{ width: '100%' }} marginTop={7}>
        <div
          className={classNames(
            'full-width-container-capped',
            'full-width-container',
            'site-header-nav-flyout-content'
          )}
        >
          <ul
            className={classNames(
              'site-header-nav-menu-items',
              'full-width-container'
            )}
          >
            {props.children
              .filter(
                (tileItem: any) =>
                  tileItem.url && tileItem.image && tileItem.image.url
              )
              .map((tileItem: any, itemIndex: number) => {
                return (
                  itemIndex < 5 && (
                    <Box
                      className="site-header-nav-flyout-tile-menu-tile"
                      marginLeft={itemIndex === 0 ? undefined : '7'}
                      marginTop={'7'}
                      paddingBottom={'7'}
                      component="li"
                      key={`${tileItem.id}`}
                    >
                      <Tile
                        tileItem={tileItem}
                        parentNavigationDetailPath={
                          props.parentNavigationDetailPath
                        }
                        dataPosition={itemIndex + 1}
                      />
                    </Box>
                  )
                );
              })}
          </ul>
        </div>
        {!isMxNav && (
          <SeeAllBar
            seeAllText={props.seeAllText}
            seeAllUrl={props.seeAllUrl}
            parentNavigationDetailPath={props.parentNavigationDetailPath}
            analyticsId={props.seeAllAnalyticsId}
          />
        )}
      </BoundedContent>
    </section>
  );

  return (
    <FlyoutWrapper
      flyoutId={props.id || `tile-list-flyout-${props.parentNavigationDetailPath.join('-')}`}
      children={props.children}
      renderFullFlyout={renderFullFlyout}
      ssrContainerClassName="site-header-nav-flyout"
      isInFocusedCategory={props.isInFocusedCategory}
    />
  );
};

const Tile = ({
  tileItem,
  parentNavigationDetailPath,
  dataPosition,
}: TileProps) => {
  return (
    <TrackableLink
      subSection="TopNav"
      navigationDetailPath={
        tileItem.analyticsId
          ? parentNavigationDetailPath.concat([tileItem.analyticsId])
          : []
      }
      linkType="Tile List Tile"
      href={tileItem.url}
      tabIndex={0}
      className={classNames('site-header-nav-secondlevel-js')}
      dataPosition={dataPosition}
    >
      <StandardTile>
        {tileItem?.badge?.text && (
          <StandardTileOverlay>
            <BookendsBadge {...tileItem.badge} layout="visual" />
          </StandardTileOverlay>
        )}
        <StandardTileImage>
          <Image image={tileItem.image} alt="" lazy />
        </StandardTileImage>
        <StandardTileContents>
          <StandardTileName>
            <Typography fontWeight="bold">{tileItem.text}</Typography>
          </StandardTileName>
          <StandardTileDescription>
            {tileItem.description}
          </StandardTileDescription>
        </StandardTileContents>
      </StandardTile>
    </TrackableLink>
  );
};
